import Color = require("color-js");
import thunkMiddleware from "redux-thunk";
import * as Actions from "./actions";
import rootReducer from "./CoreReducer";
import { toast } from "react-toastify";
import { Store, Dispatch } from "redux";
import { CoughAnalysisSetsFixedCommentsDisplay, CoughEventTypeEnum } from "../../misc/api/apiTypes";
import { timerMiddleware } from "../../misc/middleware/TimerMiddleware";
import Productionize from "../../misc/production-middleware";
import { mapToServerEvent } from "./eventMapping";
import { AnalyseState, LoadingStateEnum, PlayStateEnum } from "./interfaces/state";
import { WebPortalLegacyClientInternal } from "../../misc/api/WebPortalTokenClient";
import { MapPeriod } from "./MappingPositionHelper";
import { WarningToast } from "./components/WarningToast";
import { AudioChannel, FigureOptions, FigureType, Point, PointOptions, Segment, SegmentOptions } from "peaks.js";

export interface ServerEvent {
    AnalysisSetId: string;
    EventTypeId: string;
    RawStart?: number;
    RawEnd?: number;
    ProcessedStart?: number;
    ProcessedEnd?: number;
    SensorProcessedStart?: number;
    SensorProcessedEnd?: number;
    vtaCt1Start?: number;
    vtaCt1End?: number;
    disputed?: boolean;
}

export enum AnalysisMode {
    Analysis = "Analysis",
    Readonly = "Readonly"
}

export const DEFAULT_WAVEFORM_TYPE = "Raw";

export const initialState = (primarySetId: string, mode?: boolean, requestedViewOnly?: boolean): AnalyseState => {
    const previousPlaybackTimepoint = localStorage.getItem(
        `analysisPreviousPlaybackTimepoint_${primarySetId}`
    );
    const timepointNumber =
        previousPlaybackTimepoint !== null ? parseFloat(previousPlaybackTimepoint) : null;

    const scrollAction = localStorage.getItem("analysisScrollAction") === "true";

    const analysisMode = mode ? AnalysisMode.Readonly : requestedViewOnly ? AnalysisMode.Readonly : AnalysisMode.Analysis;

    return {
        core: {
            areKeyboardShortcutsVisible: false,
            availableEventTypes: [],
            availableFixedComments: [],
            currentTimepoint: 0,
            isAddingSessionNote: false,
            isDownloadClicked: false,
            isEventListOpen: false,
            isDisputedListOpen: false,
            isSendForReviewModalOpen: false,
            isFixedCommentsDialogOpen: false,
            isGridlineVisible: true,
            isLoadError: false,
            configErrorMessage: null,
            isMicrophoneMuted: false,
            isScrollActionZoom: scrollAction,
            isSensorMuted: false,
            isSessionNotesVisible: false,
            canAddEventByDoubleClick: mode === false ? true : false,
            loadingState: LoadingStateEnum.Initial,
            isViewOnly: analysisMode === AnalysisMode.Readonly,
            mode: analysisMode,
            peaksJs: null,
            playState: PlayStateEnum.NotStarted,
            storedPlaybackTime: timepointNumber,
            previouslySelectedWaveform: null,
            selectedWaveform: DEFAULT_WAVEFORM_TYPE,
            sessionMetadata: null,
            sessionNotes: [],
            studyId: null,
            zoomLevel: 0,
            isHourSegmentsModalOpened: false,
            hourSegmentsMap: new Map(),
            isAcceptabilityAuditModalVisible: false,
            acceptabilityStatus: null,
            isSubmitting: false,
            rawTimestampPoints: null,
            previousPlaybackTimepoint: null,
            playbackServiceStatus: null,
            positions: null,
            coughAnalysisSet: null,
            comments: null,
            blindComments: null,
            requiresVisualReview: null,
            eventDuplicateThreshold: null,
            acceptanceErrorMessage: null,
            disputedAcceptanceErrorMessage: null,
            selectedSegment: null,
            isSegmented: false,
            visualInspectionErrorMessage: null,
            audioLength: null,
            confirmedDuplicatePoints: new Set<string>(),
            adjudicationEnabled: false,
            isQCStatementsOpen: false,
        },
        analysisSet: {
            coughAnalysisSets: new Array<CoughAnalysisSetsFixedCommentsDisplay>(),
            isAnalysisSetDialogOpen: false,
            isLoaded: false,
            isMaximumAnalysisSetsAlertVisible: false,
            primaryAnalystSetId: primarySetId,
        }
    };
};

export default function configureStore(preloadedState: AnalyseState) {
    const createStoreWithMiddleware = Productionize([
        thunkMiddleware,
        peaksJSMiddleware,
        timerMiddleware
    ]);

    return createStoreWithMiddleware(rootReducer, preloadedState) as Store<AnalyseState>;
}

/**
 *
 * Verifying that the tag being added is within the bounds of the recording.
 * 
 * @param state
 * @param timePoint
 */
function VerifyTimeInsideWaveform(state: AnalyseState, timePoint: number): string {
    if ((timePoint === null || timePoint === undefined) || timePoint < 0) {
        return "Invalid Event. Events can't be added outside the cough file.";
    }

    const duration = state.core.peaksJs.player.getDuration();

    return timePoint > duration ? "Invalid Event. Events can't be added outside the cough file." : null;
}

function validateTimestamps(state: AnalyseState, figure: FigureOptions): string {
    if (figure.figureType == FigureType.Point) {
        return VerifyTimeInsideWaveform(state, (figure as Point).time);
    }

    return VerifyTimeInsideWaveform(state, (figure as Segment).startTime) || VerifyTimeInsideWaveform(state, (figure as Segment).endTime);
}

function peaksJSMiddleware(store: Store<AnalyseState>) {
    return (next: any) => async (action: Actions.Action) => {
        const state = store.getState();

        switch (action.type) {
            case Actions.ActionType.DestoryPeaks:
                if (state.core.peaksJs) {
                    state.core.peaksJs.destroy();
                }
                break;
            case Actions.ActionType.Play:
                state.core.peaksJs.player.play();
                break;
            case Actions.ActionType.Pause:
                state.core.peaksJs.player.pause();
                break;
            case Actions.ActionType.Reset:
                state.core.peaksJs.player.seek(0);
                break;
            case Actions.ActionType.JumpToEnd:
                state.core.peaksJs.player.seek(state.core.audioLength - 0.001);
                break;
            case Actions.ActionType.ToggleMuteMicrophone:
                state.core.peaksJs.player.toggleChannelMute(AudioChannel.Microphone);
                break;
            case Actions.ActionType.ToggleMuteSensor:
                state.core.peaksJs.player.toggleChannelMute(AudioChannel.Sensor);
                break;
            case Actions.ActionType.ToggleGridline:
                state.core.peaksJs.toggleGridLines('zoomview');
                break;
            case Actions.ActionType.SetZoomLevel:
                state.core.peaksJs.zoom.setZoom(action.level);
                break;
            case Actions.ActionType.AddEvent:
                if (state.core.playState === PlayStateEnum.Playing) {
                    return;
                }

                const primarySetId = state.analysisSet.primaryAnalystSetId;
                const eventType = state.core.selectedEventType;

                const set = state.analysisSet.coughAnalysisSets.find(i => i.Id === primarySetId);

                let figure: FigureOptions;
                if (!eventType.period) {
                    figure = Point.create({
                        id: action.event.id,
                        labelText: set.Type.Name,
                        editable: true,
                        color: Color(eventType.colour).setAlpha(0.6).toCSS(),
                        time: action.event.startTime,
                        eventTypeId: eventType.id,
                        eventTypeName: eventType.name
                    });
                }
                else {
                    figure = Segment.create({
                        id: action.event.id,
                        labelText: set.Type.Name,
                        editable: true,
                        color: Color(eventType.colour).setAlpha(0.6).toCSS(),
                        startTime: action.event.startTime,
                        endTime: action.event.endTime !== undefined ? action.event.endTime : (action.event.startTime + 3),
                        eventTypeId: eventType.id,
                        eventTypeName: eventType.name
                    });
                }

                // If there is already a point of the SAME TYPE, then don't add it again and inform the user.
                if (state.core.peaksJs.figures.exist(figure)) {
                    toast.error(
                        "A Marker Of Type " + eventType.name + " Already Exists At This Point."
                    );
                    return;
                }

                let err = validateTimestamps(state, figure);

                if (err) {
                    toast.error(err);
                    return;
                }

                addEventFetch(state, figure, store.dispatch, action.legacyClient);
                break;

            case Actions.ActionType.SkipBackward:
                const newTimeBackwards = state.core.peaksJs.time.getCurrentTime() - 3.5 * Math.pow(2, state.core.peaksJs.options.currentZoomLevel)
                state.core.peaksJs.time.setCurrentTime(newTimeBackwards < 0 ? 0 : newTimeBackwards);
                break;
            case Actions.ActionType.SkipForward:
                const newTimeForwards = state.core.peaksJs.time.getCurrentTime() + 3.5 * Math.pow(2, state.core.peaksJs.options.currentZoomLevel);
                state.core.peaksJs.time.setCurrentTime(newTimeForwards > state.core.audioLength ? state.core.audioLength - 0.001 : newTimeForwards);
                
                break;

            case Actions.ActionType.MoveEvent:
                if (action.figure.figureType == FigureType.Point) {
                    movePointEvent(state, action.discardedData as Partial<PointOptions>, store.dispatch, action.legacyClient);
                } else {
                    movePeriodEvent(state, action.discardedData as Partial<SegmentOptions>, store.dispatch, action.legacyClient);
                }
                break;

            case Actions.ActionType.RemovePointEvent:
                let point: Point;
                if (action.id) {
                    point = state.core.peaksJs.points.getPoint(action.id);
                } else {
                    point = state.core.peaksJs.figures.selected as Point;
                }
                await removeEventFetch(state, point, store.dispatch, action.legacyClient);
                break;
            case Actions.ActionType.RemovePeriodEvent:
                let period: Segment;
                if (action.id) {
                    period = state.core.peaksJs.segments.getSegment(action.id);
                } else {
                    period = state.core.peaksJs.figures.selected as Segment;
                }
                await removeEventFetch(state, period, store.dispatch, action.legacyClient);
                break;
            case Actions.ActionType.ResumePlaybackJump:
                state.core.peaksJs.player.seek(state.core.storedPlaybackTime - state.core.peaksJs.options.multiDayOffset);
                break;
            case Actions.ActionType.SavePlaybackPosition:
                localStorage.setItem(
                    `analysisPreviousPlaybackTimepoint_${state.analysisSet.primaryAnalystSetId}`,
                    (state.core.peaksJs.time.getCurrentTime() + state.core.peaksJs.options.multiDayOffset).toString()
                );
                break;
            case Actions.ActionType.ToggleScrollAction:
                localStorage.setItem(
                    "analysisScrollAction",
                    (!state.core.isScrollActionZoom).toString()
                );
                break;
            case Actions.ActionType.ChangeWaveform:
                action.legacyClient.changeWaveform(
                    state.core.sessionMetadata.sessionId,
                    state.analysisSet.primaryAnalystSetId,
                    state.core.isViewOnly,
                    action.waveformToChangeTo
                );
                break;
            case Actions.ActionType.JumpToTimepoint:
                state.core.peaksJs.player.seek(action.timepoint);

                let figures = state.core.peaksJs.points.filter({time: action.timepoint}) as any;
                if (figures.length == 0) {
                    figures = state.core.peaksJs.segments.filter({startTime: action.timepoint});
                }

                if (figures.length != 0) {
                    state.core.peaksJs.figures.select(figures[0]);
                }

                break;
            case Actions.ActionType.SetDuplicateVerified:
                state.core.peaksJs.points.getPoint(action.id).update({confirmedValidDuplicate: true});
                break;
            case Actions.ActionType.UpdateAnalysisSetEventLocally:
                let peaks = state.core.peaksJs;
                let pointEvent = peaks.points.getPoint(action.payload.id);

                pointEvent.update({ disputedAccepted: action.payload.disputedAccepted, disputedComment: action.payload.disputedComment });
                break;
            default:
        }
        return next(action);
    };
}

function movePointEvent(state: AnalyseState, discardedData: Partial<PointOptions>, dispatch: Dispatch<AnalyseState>, legacyClient: WebPortalLegacyClientInternal) {
    let point = state.core.peaksJs.figures.selected as Point;
    const eventType = state.core.availableEventTypes.find(x => x.id === point.eventTypeId);

    const multiDayOffset = state.core.peaksJs.options.multiDayOffset || 0;
    point.update({time: point.time + multiDayOffset});

    // If there is already a point of the SAME TYPE, then editing the point to arrive at this position is not a valid edit.
    const existingPoints = state.core.peaksJs.points.getPoints().filter(e => e.id != point.id && e.time == point.time);
    if (existingPoints.length != 0) {
        toast.error("A Marker Of Type " + eventType.name + " Already Exists At This Point.");
        return;
    }

    removeEventFetch(state, point, dispatch, legacyClient, true).then((oldEvent) => {
        addEventFetch(state, point, dispatch, legacyClient).then((response) => {
            if (response?.Exception) {
                point.update({time: discardedData.time});
                addEventFetch(state, point, dispatch, legacyClient);
            }
        });
    });
}

function movePeriodEvent(state: AnalyseState, discardedData: Partial<SegmentOptions>, dispatch: Dispatch<AnalyseState>, legacyClient: WebPortalLegacyClientInternal) {
    let period = state.core.peaksJs.figures.selected as Segment;

    const multiDayOffset = state.core.peaksJs.options.multiDayOffset || 0;
    period.update({startTime: period.startTime + multiDayOffset, endTime: period.endTime + multiDayOffset});

    return removeEventFetch(state, period, dispatch, legacyClient, true).then((oldEvent) => {
        return addEventFetch(state, period, dispatch, legacyClient).then((response) => {
            if (response?.Exception) {
                period.update({startTime: discardedData.startTime, endTime: discardedData.endTime});
                addEventFetch(state, period, dispatch, legacyClient);
            }
        });
    });
}

function addEventFetch(
    state: AnalyseState,
    cliEvent: FigureOptions,
    dispatch: Dispatch<AnalyseState>,
    legacyClient: WebPortalLegacyClientInternal
) {
    toast.info("Marking " + cliEvent.eventTypeLabel + ".");

    // The eventTypeId is used as the user may have selected another event in the dropdown whiled editing another type of point.
    // It cannot be assumed that when an event is added/removed that the event type is the type currently chosen.

    try {
        const eventType = state.core.availableEventTypes.find(x => x.id === cliEvent.eventTypeId);
        const serverEvent = mapToServerEvent(cliEvent, state.analysisSet.primaryAnalystSetId, cliEvent.eventTypeId, state.core.selectedWaveform);
        const eventMapping = MapPeriod(state.core.positions, serverEvent.RawStart, serverEvent.RawEnd, serverEvent.ProcessedStart,
                                       serverEvent.ProcessedEnd, serverEvent.SensorProcessedStart, serverEvent.SensorProcessedEnd,
                                       serverEvent.vtaCt1Start, serverEvent.vtaCt1End, state.core.selectedSegment);

        return legacyClient.putCoughAnalysisSetEvent({
            AnalysisSetId: serverEvent.AnalysisSetId,
            EventTypeId: serverEvent.EventTypeId,
            RawStart: eventMapping.rawStart,
            RawEnd: eventMapping.rawEnd,
            ProcessedStart: eventMapping.processedStart,
            ProcessedEnd: eventMapping.processedEnd,
            SensorProcessedStart: eventMapping.sensorProcessedStart,
            SensorProcessedEnd: eventMapping.sensorProcessedEnd,
            VtaCt1Start: eventMapping.vtaCt1Start,
            VtaCt1End: eventMapping.vtaCt1End,
            SegmentOffset: ((state.core.selectedSegment * 24) - 24) * 60 * 60
        })
        .then(json => {
            const peaks = state.core.peaksJs;

            if (eventType.period) {
                let segment = peaks.segments.getSegment(cliEvent.id);

                if (segment) {
                    segment.update({ id: json.Id, rawStartTime: json.RawStart, rawEndTime: json.RawEnd });
                    segment = peaks.segments.getSegment(json.Id);
                } else {
                    segment = peaks.segments.add(Segment.create({...cliEvent, id: json.Id, rawStartTime: json.RawStart, rawEndTime: json.RawEnd }));
                }

                peaks.refresh();

                toast.success(segment.eventTypeLabel + " marked.");
                return;
            }

            const coughPeakType = state.core.availableEventTypes.find(x => x.refEventTypeId === CoughEventTypeEnum.CoughPeak);
            const matchingPoints = peaks.points.getPoints().filter(p => p.eventTypeId === coughPeakType.id && p.layerPosition === 0 && Math.abs((state.core.rawTimestampPoints.get(p.id) * 1000) - (json.RawStart * 1000)) <= state.core.eventDuplicateThreshold);
            const anyMatch = json.EventTypeId === coughPeakType.id ? matchingPoints.length > 0 : false;

            let point = peaks.points.getPoint(cliEvent.id);
            state.core.rawTimestampPoints.set(json.Id, json.RawStart);
            
            if (point) {
                point.update({id: json.Id, rawTimestamp: json.RawStart, withinDuplicateThreshold: anyMatch });
                point = peaks.points.getPoint(json.Id);
            } else {
                point = peaks.points.add(Point.create({...cliEvent, id: json.Id, rawTimestamp: json.RawStart, withinDuplicateThreshold: anyMatch } as any));
            }

            peaks.refresh();

            if (anyMatch) {
                for (const matchPoint of matchingPoints) {
                    peaks.points.getPoint(matchPoint.id).update({withinDuplicateThreshold: true});
                }

                toast.warn(t => WarningToast({
                    closeToast: t.closeToast,
                    data: t.data, toastProps: t.toastProps,
                    eventTimeText: cliEvent.eventTypeLabel,
                    point: state.core.peaksJs.points.getPoint(cliEvent.id)
                }), {
                    toastId: cliEvent.id,
                    autoClose: false,
                    closeOnClick: false,
                    draggable: false,
                    closeButton: false,

                    data: {
                        onUndo: () => dispatch(
                            Actions.RemovePointEventAction.create(
                                legacyClient,
                                json.Id
                            )),
                        onJumpTo: (timepoint) => dispatch(
                            Actions.JumpToTimePointAction.create(
                                timepoint
                            ))
                    }
                });
            } else {
                toast.success(point.eventTypeLabel + " marked.");
            }
        })
        .catch(error => { toast.error(`Failed marking ${eventType.name}. ${error.Message}`); return error; })
    }
    catch (error) {
        toast.error(error.message);
        return error;
    }
}

function removeEventFetch(
    state: AnalyseState,
    event: Point | Segment,
    dispatch: Dispatch<AnalyseState>,
    legacyClient: WebPortalLegacyClientInternal,
    addFollows: boolean = false
) {
    toast.dismiss(event.id);

    return legacyClient.deleteCoughEvent({ Id: event.id })
    .then(response => {
        if (!addFollows) {
            const peaks = state.core.peaksJs;

            peaks.figures.remove(event);
            peaks.refresh();
        }

        return response.json();
    })
    .catch(_ => {
        const eventType = state.core.availableEventTypes.find(x => x.id === event.eventTypeId);
        toast.error(`Failed to remove ${eventType.name}.`)
    });
}
